import 'projects/sharedComponents/src/healthSummaryCard/healthSummaryCard.scss';

import { Box, Button, Card, CardContent, Grid, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  CareResourcesReasonCategory,
  HealthSeverity,
  HealthSummaryCardFragment,
} from 'projects/sharedComponents/src/generated/graphql-client-types';
import { buildEmbarkCDNUrl, buildMyEvLegacyLink } from 'projects/sharedComponents/src/services/links/linkBuilder';

import { CareResourcesResultsBanner } from 'projects/sharedComponents/src/pet/careResources/resultsBanner/careResourcesResultsBanner';
import { FC } from 'react';
import { HealthSummaryCardList } from 'projects/sharedComponents/src/healthSummaryCard/healthSummaryCardList';
import { RenderIf } from 'projects/sharedComponents/src/renderStates/renderIf';

type HealthSummaryCardProps = {
  dog: HealthSummaryCardFragment;
  isBreeder?: boolean;
  shouldShowViewButtons?: boolean;
  shouldShowCareResourcesBanner?: boolean;
};

export const HealthSummaryCard: FC<HealthSummaryCardProps> = ({
  dog,
  shouldShowViewButtons = false,
  isBreeder,
  shouldShowCareResourcesBanner = false,
}) => {
  const theme = useTheme();
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));

  if (!dog.health || !dog.health?.healthScoreCard) {
    return <></>;
  }
  const logos = {
    good: {
      outline: buildEmbarkCDNUrl('/img/health/outline/good-status-icon.svg'),
      filled: buildEmbarkCDNUrl('/img/health/filled/good-status-icon.svg'),
    },
    warn: {
      outline: buildEmbarkCDNUrl('/img/health/outline/warn-status-icon.svg'),
      filled: buildEmbarkCDNUrl('/img/health/filled/warn-status-icon.svg'),
    },
    danger: {
      outline: buildEmbarkCDNUrl('/img/health/outline/danger-status-icon.svg'),
      filled: buildEmbarkCDNUrl('/img/health/filled/danger-status-icon.svg'),
    },
    noResult: {
      outline: buildEmbarkCDNUrl('/img/health/outline/noResult-status-icon.svg'),
      filled: buildEmbarkCDNUrl('/img/health/filled/noResult-status-icon.svg'),
    },
  };

  const getSummaryLogo = (): string => {
    switch (dog.health?.healthScoreCard.severity) {
      case 'good': {
        return logos.good.outline;
      }
      case 'warn': {
        return logos.warn.outline;
      }
      case 'danger': {
        return logos.danger.outline;
      }
      default: {
        return logos.noResult.outline;
      }
    }
  };
  const summaryLogo = getSummaryLogo();

  let reportText =
    'Email your veterinarian a copy of your dog’s Embark results to help inform their diagnosis and potential treatment.';
  let reportButtonText = 'SEND REPORT TO VET';
  let reportLink = buildMyEvLegacyLink(`/members/results/veterinary-report?i=${dog.petNum}`);

  if (dog.showOfaReport && isBreeder) {
    reportText =
      'You can submit your dog’s report to the OFA along with payment to the OFA and a completed OFA DNA application form.';
    reportButtonText = 'DOWNLOAD OFA REPORT';
    reportLink = buildMyEvLegacyLink(`/members/results/ofa-report?i=${dog.petNum}`);
  }

  const hasDangerHealthResults = dog.health.healthScoreCard.dangerHealthResults.length > 0;

  const hasWarnHealthResults = dog.health.healthScoreCard.warnHealthResults.length > 0;

  const hasNoCallHealthResults = dog.health.healthScoreCard.noResultHealthResults.length > 0;

  return (
    <Card className="healthSummaryCard">
      <CardContent>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={3} sm={2} md={1}>
            <Box display="flex" justifyContent="center" className="healthSummaryCard-summaryLogo">
              <img src={summaryLogo} alt="" height={40} />
            </Box>
          </Grid>
          <Grid item xs={9} sm={10} md={11}>
            <Box>
              <Typography className="healthSummaryCard-summaryText">
                <strong>{dog.health.healthScoreCard.description}</strong>
              </Typography>
              <RenderIf condition={!!dog.health.healthScoreCard.subline}>
                <Typography className="healthSummaryCard-summarySubText">
                  {dog.health.healthScoreCard.subline}
                </Typography>
              </RenderIf>
            </Box>
          </Grid>
          {hasDangerHealthResults && (
            <Grid item xs={12} className="healthSummaryCard-dangerList">
              <HealthSummaryCardList
                healthResults={dog.health.healthScoreCard.dangerHealthResults}
                logoUrl={logos.danger.filled}
                severity={HealthSeverity.Danger}
              />
            </Grid>
          )}
          {hasWarnHealthResults && (
            <Grid item xs={12} className="healthSummaryCard-warnList">
              <HealthSummaryCardList
                healthResults={dog.health.healthScoreCard.warnHealthResults}
                logoUrl={logos.warn.filled}
                severity={HealthSeverity.Warn}
              />
            </Grid>
          )}
          {hasNoCallHealthResults && (
            <Grid item xs={12} className="healthSummaryCard-noResultsList">
              <HealthSummaryCardList
                healthResults={dog.health.healthScoreCard.noResultHealthResults}
                logoUrl={logos.noResult.filled}
                severity={HealthSeverity.NoResult}
              />
            </Grid>
          )}
          <Grid item xs={12} className="healthSummaryCard-quickList">
            <HealthSummaryCardList
              healthResults={dog.health.healthScoreCard.quickLinks}
              logoUrl={logos.good.filled}
              severity={HealthSeverity.Good}
            />
          </Grid>
          <RenderIf condition={!shouldShowViewButtons}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems="center"
              sx={{ marginTop: '8px', paddingLeft: '18px', paddingRight: '18px' }}
            >
              <Typography className="healthSummaryCard-emailVetText">{reportText}</Typography>
              <div>
                <Button
                  className="healthSummaryCard-sendVetReportButton"
                  variant="contained"
                  color="primary"
                  href={reportLink}
                  fullWidth={isExtraSmallScreen}
                  sx={{ display: 'block' }}
                >
                  {reportButtonText}
                </Button>
              </div>
            </Stack>
          </RenderIf>
          <RenderIf condition={shouldShowViewButtons}>
            <Grid item xs={12} sm={12} className="healthSummaryCard-viewButtons">
              <Button
                className="healthSummaryCard-viewHealthResults"
                variant="contained"
                color="primary"
                href={buildMyEvLegacyLink(`/members/results/health?i=${dog.petNum}`)}
              >
                View Health Details
              </Button>
              <Button className="healthSummaryCard-sendVet" variant="outlined" color="secondary" href={reportLink}>
                {reportButtonText}
              </Button>
            </Grid>
          </RenderIf>
        </Grid>
      </CardContent>
      <RenderIf condition={shouldShowCareResourcesBanner}>
        <CareResourcesResultsBanner pet={dog} category={CareResourcesReasonCategory.Health} />
      </RenderIf>
    </Card>
  );
};
